import { Link } from "react-router-dom";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faEllipsis, faEye, faPaperPlane, faRotate } from "@fortawesome/pro-regular-svg-icons";

import { ROUTE_CONFIG } from "@/routes/config";
import { Icon } from "@/components";
import { AnchoredPopover } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AnchoredPopover";

import { Webhook } from "../../schema/webhook";
import { WebhookSigningActions } from "./useWebhookSigningDialogs";

interface MenuRowProps {
	icon: typeof faRotate;
	label: string;
	testId: string;
	suffix?: string;
	onClick: () => void;
}

const MenuRow: FunctionComponent<MenuRowProps> = ({ icon, label, testId, suffix, onClick }) => (
	<button
		className="flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-medium text-text hover:bg-background-light"
		data-test={testId}
		type="button"
		onClick={onClick}
	>
		<Icon className="h-3.5 w-3.5 shrink-0 opacity-70" icon={icon} />
		<span className="truncate">{label}</span>
		{suffix && <span className="text-xs font-normal text-text-meta">{suffix}</span>}
	</button>
);

/**
 * A webhook row's signing actions (PREC-4249): send a test event, rotate the secret, and — only
 * while a secret nobody has seen exists (a webhook from before signing) — reveal it once.
 */
export const WebhookRowMenu: FunctionComponent<{
	webhook: Webhook;
	actions: WebhookSigningActions;
}> = ({ webhook, actions }) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const close = () => setAnchorEl(null);
	const pick = (action: () => unknown) => () => {
		close();
		action();
	};

	return (
		<>
			<button
				aria-label={t("webhookSigning.menu.open")}
				className="cursor-pointer"
				data-test="webhook-row-menu"
				type="button"
				onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
			>
				<Icon className="text-text-meta w-4 h-4" icon={faEllipsis} size="lg" />
			</button>
			{anchorEl && (
				<AnchoredPopover anchorEl={anchorEl} width={220} onClose={close}>
					<div className="overflow-hidden rounded-xl border border-border-light bg-white py-1 shadow-lg">
						<Link
							className="flex cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-medium text-text hover:bg-background-light"
							to={ROUTE_CONFIG.INTEGRATIONS_WEBHOOK_DELIVERIES.replace(
								":webhookId",
								String(webhook.id)
							)}
							onClick={close}
						>
							<Icon className="h-3.5 w-3.5" icon={faEye} />
							{t("webhookHealth.deliveries.title")}{" "}
							<span className="text-xs text-brand-light">{t("webhookHealth.deliveries.new")}</span>
						</Link>
						<MenuRow
							icon={faPaperPlane}
							label={t("webhookSigning.menu.sendTestEvent")}
							testId="webhook-menu-test-event"
							onClick={pick(() => actions.sendTestEvent(webhook))}
						/>
						<MenuRow
							icon={faRotate}
							label={t("webhookSigning.menu.rotateSecret")}
							testId="webhook-menu-rotate"
							onClick={pick(() => actions.rotateSecret(webhook))}
						/>
						{webhook.signing?.revealable && (
							<MenuRow
								icon={faEye}
								label={t("webhookSigning.menu.revealSecret")}
								suffix={t("webhookSigning.menu.once")}
								testId="webhook-menu-reveal"
								onClick={pick(() => actions.revealSecret(webhook))}
							/>
						)}
					</div>
				</AnchoredPopover>
			)}
		</>
	);
};
